import { QueryClient, QueryClientProvider, useQuery } from '@tanstack/react-query';
import { lazy, Suspense } from 'react';
import { BrowserRouter, Navigate, Outlet, Route, Routes, useLocation } from 'react-router';
import { Toaster } from 'sonner';
import { TooltipProvider } from '@/components/ui/tooltip';
import { api } from '@/lib/api';
import { AppShell } from '@/components/layout/app-shell';

const LoginPage = lazy(() => import('@/pages/login'));
const RegisterPage = lazy(() => import('@/pages/register'));
const MagicPage = lazy(() => import('@/pages/magic'));
const OnboardingPage = lazy(() => import('@/pages/onboarding'));
const OverviewPage = lazy(() => import('@/pages/overview'));
const ProjectsPage = lazy(() => import('@/pages/projects'));
const AccountsPage = lazy(() => import('@/pages/accounts'));
const BillingPage = lazy(() => import('@/pages/billing'));
const UsersPage = lazy(() => import('@/pages/users'));
const ProjectLaunchPage = lazy(() => import('@/pages/project-launch'));
const ProjectGoalsPage = lazy(() => import('@/pages/project-goals'));
const ProjectActivityGoalsPage = lazy(() => import('@/pages/project-activity-goals'));
const ProjectRunsPage = lazy(() => import('@/pages/project-runs'));
const RunDetailsPage = lazy(() => import('@/pages/run-details'));
const ProjectControlPage = lazy(() => import('@/pages/project-control'));
const ProjectSettingsPage = lazy(() => import('@/pages/project-settings'));

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: { staleTime: 15_000, retry: 1, refetchOnWindowFocus: false },
    mutations: { retry: 0 },
  },
});

function LoadingScreen() {
  return <div className="grid min-h-[45vh] place-items-center text-sm text-muted-foreground">Загружаем…</div>;
}

function Protected() {
  const location = useLocation();
  const auth = useQuery({ queryKey: ['auth'], queryFn: api.auth });
  if (auth.isPending) return <LoadingScreen />;
  if (!auth.data?.authenticated) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  return <Outlet />;
}

function PublicOnly() {
  const auth = useQuery({ queryKey: ['auth'], queryFn: api.auth });
  if (auth.isPending) return <LoadingScreen />;
  if (auth.data?.authenticated) return <Navigate to="/" replace />;
  return <Outlet />;
}

function LegacyProjectRedirect({ target }: { target: string }) {
  const path = location.pathname.split('/');
  return <Navigate to={`/projects/${path[2]}/${target}`} replace />;
}

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <BrowserRouter>
          <Suspense fallback={<LoadingScreen />}>
            <Routes>
              <Route element={<PublicOnly />}>
                <Route path="/login" element={<LoginPage />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/auth/magic" element={<MagicPage />} />
              </Route>
              <Route element={<Protected />}>
                <Route path="/projects/new" element={<OnboardingPage />} />
                <Route element={<AppShell />}>
                  <Route path="/" element={<OverviewPage />} />
                  <Route path="/app" element={<Navigate to="/" replace />} />
                  <Route path="/projects" element={<ProjectsPage />} />
                  <Route path="/accounts" element={<AccountsPage />} />
                  <Route path="/billing" element={<BillingPage />} />
                  <Route path="/admin/users" element={<UsersPage />} />
                  <Route path="/projects/:projectId" element={<Navigate to="launch" replace />} />
                  <Route path="/projects/:projectId/launch" element={<ProjectLaunchPage />} />
                  <Route path="/projects/:projectId/goals" element={<ProjectGoalsPage />} />
                  <Route path="/projects/:projectId/activity-goals" element={<ProjectActivityGoalsPage />} />
                  <Route path="/projects/:projectId/runs" element={<ProjectRunsPage />} />
                  <Route path="/projects/:projectId/runs/:runId" element={<RunDetailsPage />} />
                  <Route path="/projects/:projectId/control" element={<ProjectControlPage />} />
                  <Route path="/projects/:projectId/settings" element={<ProjectSettingsPage />} />
                  <Route path="/projects/:projectId/overview" element={<LegacyProjectRedirect target="launch" />} />
                  <Route path="/projects/:projectId/activities" element={<LegacyProjectRedirect target="goals" />} />
                  <Route path="/projects/:projectId/logs" element={<LegacyProjectRedirect target="runs" />} />
                  <Route path="/projects/:projectId/diagnostics" element={<LegacyProjectRedirect target="control" />} />
                </Route>
              </Route>
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
      </TooltipProvider>
    </QueryClientProvider>
  );
}
